/**
 * loans.ts — the pure view helpers of the loans screen (REDESIGN §6).
 *
 * `GET /api/loans` already delivers every FIGURE: totals, the weighted rate, the annual-interest
 * estimate, the 13-point debt trend, `endingSoon` and the card grouping (`LoansResponse`). None
 * of that is recomputed here — the server is the one implementation, exactly as the interest and
 * amortization rules require.
 *
 * What is left for the client is presentation arithmetic the wire cannot carry:
 *   · which rows the `הכל · פרטי · עסקי` control is showing, and their header figures;
 *   · where each end date sits on the 5-year strip (a pixel ratio, not a money figure);
 *   · the y-axis step of the trend chart.
 *
 * All of it is pure and takes `asOf` from the payload — never a wall clock.
 */

import type {
  AccountId,
  DayString,
  LoanAssignment,
  LoanCardRef,
  LoanKind,
  LoanRow,
  LoanSort,
  LoansTotals,
  LoansResponse,
  ProviderId,
} from '../../shared-types.ts';
import { daysBetween } from './dates.ts';

/** The segmented control of §6: `הכל` plus the two assignments. */
export type LoanScope = 'all' | LoanAssignment;
export const LOAN_SCOPES: readonly LoanScope[] = ['all', 'personal', 'business'];

/** `LoanSort` values, in the order the switch shows them. §6's default is `rate`. */
export const LOAN_SORT_ORDER: readonly LoanSort[] = ['rate', 'balance', 'payment', 'end'];
export const DEFAULT_LOAN_SORT: LoanSort = 'rate';

export function isLoanScope(value: string | undefined): value is LoanScope {
  return typeof value === 'string' && (LOAN_SCOPES as readonly string[]).includes(value);
}

export function isLoanSort(value: string | undefined): value is LoanSort {
  return typeof value === 'string' && (LOAN_SORT_ORDER as readonly string[]).includes(value);
}

/** The rows the scope shows. `items` arrives already sorted by the server's `?sort=`. */
export function filterByScope(rows: readonly LoanRow[], scope: LoanScope): LoanRow[] {
  if (scope === 'all') return [...rows];
  return rows.filter((row) => row.assignment === scope);
}

/**
 * The header figures for the current scope. `הכל` uses the payload's own totals; a single
 * assignment uses the `byAssignment` block the server already computed for it — so the screen
 * never re-adds money client-side.
 */
export function scopeTotals(response: LoansResponse, scope: LoanScope): LoansTotals {
  return scope === 'all' ? response.totals : response.byAssignment[scope];
}

// ───────────────────────────── the ranking (§6) ─────────────────────────────

/** How many rows a group shows before `עוד N` (REDESIGN §6 — 19 open loans must not be one wall). */
export const RANK_GROUP_LIMIT = 5;

/** One card's facilities: `Cal ***8993 · 10 מסגרות · יתרה ₪X · ₪Y לחודש`, then its rows. */
export interface LoanSubGroup {
  /** Stable React key and `aria-controls` id. */
  key: string;
  /** `null` on the bank group — only a card facility hangs off a card. */
  card: LoanCardRef | null;
  rows: LoanRow[];
  count: number;
  balanceAgorot: number;
  monthlyPaymentAgorot: number;
}

/** §6 keeps card facilities out of the bank list; the groups themselves follow the active sort. */
export interface LoanRankGroup {
  kind: LoanKind;
  subs: LoanSubGroup[];
  count: number;
  balanceAgorot: number;
  monthlyPaymentAgorot: number;
}

/**
 * The most expensive open loan across every group — the one figure `הכי יקר קודם` exists to name.
 * Rows arrive in the server's order, but that order is per-group once §6's grouping is applied,
 * so the callout above the groups is what actually answers "מה הכי יקר".
 */
export function topRateRow(rows: readonly LoanRow[]): LoanRow | null {
  let best: LoanRow | null = null;
  for (const row of rows) {
    if (row.ratePct === null) continue;
    if (best === null || row.ratePct > best.ratePct! || (row.ratePct === best.ratePct! && row.balanceAgorot > best.balanceAgorot)) {
      best = row;
    }
  }
  return best;
}

/** The value a group is ranked by, under the active sort. Higher sorts first; `null` sorts last. */
function groupRank(rows: readonly LoanRow[], sort: LoanSort): number | null {
  let best: number | null = null;
  for (const row of rows) {
    let value: number | null;
    switch (sort) {
      case 'balance':
        value = Math.abs(row.balanceAgorot);
        break;
      case 'payment':
        value = row.monthlyPaymentAgorot;
        break;
      case 'end':
        // Earlier end date first → negate the day number so "higher is better" holds everywhere.
        value = row.endDate === null ? null : -Number(row.endDate.replace(/-/g, ''));
        break;
      default:
        value = row.ratePct;
    }
    if (value === null) continue;
    if (best === null || value > best) best = value;
  }
  return best;
}

function byRankDesc(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
}

function totalsOfRows(rows: readonly LoanRow[]): { count: number; balanceAgorot: number; monthlyPaymentAgorot: number } {
  let balanceAgorot = 0;
  let monthlyPaymentAgorot = 0;
  for (const row of rows) {
    balanceAgorot += Math.abs(row.balanceAgorot);
    monthlyPaymentAgorot += row.monthlyPaymentAgorot ?? 0;
  }
  return { count: rows.length, balanceAgorot, monthlyPaymentAgorot };
}

/**
 * §6's two rules at once. Card facilities stay "grouped under their card … never mixed with bank
 * loans", AND `הכי יקר קודם` still leads: the GROUPS are ordered by the active sort's best value,
 * so on the live data the 9–12% Cal facilities sit above the 6–9% Discount loans instead of under
 * them. Rows keep the server's order inside each group; the row order is never recomputed here.
 */
export function rankGroups(rows: readonly LoanRow[], sort: LoanSort): LoanRankGroup[] {
  const bank = rows.filter((row) => row.kind === 'bank');
  const cards = rows.filter((row) => row.kind === 'card-credit');
  const groups: LoanRankGroup[] = [];

  if (bank.length) {
    groups.push({ kind: 'bank', subs: [{ key: 'bank', card: null, rows: bank, ...totalsOfRows(bank) }], ...totalsOfRows(bank) });
  }

  if (cards.length) {
    // One sub-group per parent card (`row.card`), falling back to the provider when the facility
    // matches no CARD account — never a flat wall of 12 rows under one heading.
    const byCard = new Map<string, { card: LoanCardRef | null; rows: LoanRow[] }>();
    for (const row of cards) {
      const key = row.card?.accountId ?? `provider:${row.providerId}`;
      const entry = byCard.get(key) ?? { card: row.card, rows: [] };
      entry.rows.push(row);
      byCard.set(key, entry);
    }
    const subs: LoanSubGroup[] = [...byCard.entries()].map(([key, entry]) => ({
      key,
      card: entry.card,
      rows: entry.rows,
      ...totalsOfRows(entry.rows),
    }));
    subs.sort((a, b) => byRankDesc(groupRank(a.rows, sort), groupRank(b.rows, sort)) || b.balanceAgorot - a.balanceAgorot);
    groups.push({ kind: 'card-credit', subs, ...totalsOfRows(cards) });
  }

  groups.sort((a, b) => {
    const rank = byRankDesc(
      groupRank(a.subs.flatMap((s) => s.rows), sort),
      groupRank(b.subs.flatMap((s) => s.rows), sort),
    );
    return rank || b.balanceAgorot - a.balanceAgorot;
  });
  return groups;
}

// ───────────────────────────── the end-date timeline (§6) ─────────────────────────────

export interface TimelineMark {
  accountId: AccountId;
  name: string;
  providerId: ProviderId;
  endDate: DayString;
  monthlyPaymentAgorot: number | null;
  /** 0..1 across the strip: `asOf` is 0, 31.12 of the last year is 1. */
  position: number;
}

export interface LoanTimeline {
  fromYear: number;
  toYear: number;
  years: number[];
  marks: TimelineMark[];
  /** Loans that end after the strip. */
  beyond: number;
  /** Loans with no end date at all. */
  undated: number;
}

/**
 * §6: "when each loan ends, as a horizontal strip of the next 5 years with a marker per loan,
 * so 'what frees up when' is visible". Positions only — the sentence under the strip is
 * `LoansResponse.endingSoon`, computed by the server.
 */
export function endTimeline(rows: readonly LoanRow[], asOf: DayString, years = 5): LoanTimeline {
  const fromYear = Number(asOf.slice(0, 4));
  const toYear = fromYear + years - 1;
  const lastDay = `${toYear}-12-31`;
  const span = Math.max(1, daysBetween(asOf, lastDay));
  const marks: TimelineMark[] = [];
  let beyond = 0;
  let undated = 0;

  for (const row of rows) {
    if (row.endDate === null) {
      undated += 1;
      continue;
    }
    if (row.endDate > lastDay) {
      beyond += 1;
      continue;
    }
    marks.push({
      accountId: row.accountId,
      name: row.name,
      providerId: row.providerId,
      endDate: row.endDate,
      monthlyPaymentAgorot: row.monthlyPaymentAgorot,
      position: Math.min(1, Math.max(0, daysBetween(asOf, row.endDate)) / span),
    });
  }

  marks.sort((a, b) =>
    a.endDate < b.endDate ? -1 : a.endDate > b.endDate ? 1 : a.accountId < b.accountId ? -1 : a.accountId > b.accountId ? 1 : 0,
  );
  const list: number[] = [];
  for (let year = fromYear; year <= toYear; year += 1) list.push(year);
  return { fromYear, toYear, years: list, marks, beyond, undated };
}

// ───────────────────────────── chart scale ─────────────────────────────

/**
 * A round y-axis step for the debt line: about four gridlines over the range, snapped to
 * 1/2/5 × 10ⁿ shekels so two renders of the same data never draw different axes.
 */
export function trendStep(values: readonly number[]): number {
  const finite = values.filter((value) => Number.isFinite(value));
  if (finite.length === 0) return 1;
  const range = Math.max(1, Math.max(...finite) - Math.min(...finite));
  const raw = range / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(1, raw))));
  for (const factor of [1, 2, 5, 10]) {
    const step = magnitude * factor;
    if (step >= raw) return step;
  }
  return magnitude * 10;
}
